import { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AuthContext = createContext(null);

/** Attach (or clear) the current user on the Sentry scope so any subsequent
 *  error events tag who hit them. We intentionally only send the opaque
 *  user_id + username — no email / IP / PII. Lines up with `sendDefaultPii=false`
 *  in src/sentry.js. */
function syncSentryUser(user) {
    try {
        if (user?.user_id) {
            Sentry.setUser({ id: user.user_id, username: user.username });
        } else {
            Sentry.setUser(null);
        }
    } catch (_e) { /* SDK not initialized — no-op */ }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            // /auth/me now returns null (HTTP 200) when there's no session —
            // see backend/routers/auth.py. Treat null exactly like the old 401
            // path: clear user + Sentry scope, bail before running any of the
            // post-auth side effects (referral attribution, demo redeem, etc).
            if (!data) {
                setUser(null);
                syncSentryUser(null);
                return;
            }
            setUser(data);
            syncSentryUser(data);
            // iter167l — OAuth-only users (no password ever set) get
            // auto-restore via a server-issued `chat_recovery_secret`.
            // Password users handle this in LoginPage.submit (with the actual
            // password — stronger), so we skip here when `has_password` is true.
            if (data?.chat_recovery_secret && data.has_password === false) {
                (async () => {
                    try {
                        const { silentRestoreOrBackupKeyBundle } = await import("../lib/chatKeyRecovery");
                        await silentRestoreOrBackupKeyBundle(data.user_id, data.chat_recovery_secret);
                    } catch { /* best-effort */ }
                })();
            }
            // Apply server-side language preference if the user has one.
            // We only update i18next when it differs from the active language to avoid
            // a re-render flicker on every refresh().
            try {
                if (data?.language) {
                    const i18n = (await import("../i18n")).default;
                    const current = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
                    if (current !== data.language) {
                        await i18n.changeLanguage(data.language);
                        try { localStorage.setItem("hc_lang", data.language); } catch { /* noop */ }
                    }
                }
            } catch { /* noop — i18n is best-effort */ }
            // Best-effort: if we have a pending referral code stored from a `?ref=` URL,
            // try to attribute it now that we know who's logged in. The server is the
            // source of truth for self-referral / already-attributed / unknown referrer.
            const pendingRef = (() => { try { return localStorage.getItem("hc_pending_ref"); } catch { return null; } })();
            if (pendingRef && data?.username && pendingRef !== data.username) {
                try {
                    const r = await api.post("/me/referral/track", { ref: pendingRef });
                    if (r?.data?.tracked) {
                        // Single-shot — clear once tracked OR ignored as duplicate.
                        try { localStorage.removeItem("hc_pending_ref"); } catch { /* noop */ }
                    } else {
                        // Already referred or already Pro — clear so we stop retrying every refresh.
                        try { localStorage.removeItem("hc_pending_ref"); } catch { /* noop */ }
                    }
                } catch { /* unknown referrer or self — clear so we don't keep nagging */
                    try { localStorage.removeItem("hc_pending_ref"); } catch { /* noop */ }
                }
            }
            // Demo puzzle bonus: if visitor solved one or more landing-page challenges,
            // redeem the credits on first auth. Streak ≥ 3 unlocks the +5 + puzzle-master badge
            // tier (server is the source of truth — see /api/me/redeem-demo).
            const demoUnlocked = (() => { try { return localStorage.getItem("hc_demo_unlocked"); } catch { return null; } })();
            const streak = (() => {
                try {
                    const raw = localStorage.getItem("hc_puzzle_solved_set");
                    if (!raw) return 0;
                    const arr = JSON.parse(raw);
                    return Array.isArray(arr) ? arr.length : 0;
                } catch { return 0; }
            })();
            if (demoUnlocked) {
                try {
                    const answer = (() => { try { return localStorage.getItem("hc_demo_answer") || ""; } catch { return ""; } })();
                    const r = await api.post("/me/redeem-demo", { token: demoUnlocked, answer, streak });
                    if (r?.data?.redeemed && r.data.badge === "puzzle-master") {
                        toast.success(`🏆 Puzzle Master unlocked · +${r.data.hint_credits} hint credits`);
                    } else if (r?.data?.redeemed) {
                        toast.success(`+${r.data.hint_credits} hint credit unlocked`);
                    }
                } catch { /* already claimed or invalid — ignore */ }
                try {
                    localStorage.removeItem("hc_demo_unlocked");
                    localStorage.removeItem("hc_demo_answer");
                    localStorage.removeItem("hc_puzzle_solved_set");
                } catch { /* noop */ }
            }
        } catch {
            setUser(null);
            syncSentryUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* noop */ }
        setUser(null);
        syncSentryUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
